import api from "../api";

const toResults = (data) => (Array.isArray(data?.data) ? data.data : []);

export const isWindowClosedError = (error) =>
  error?.response?.status === 409 && Boolean(error?.response?.data?.window_closed);

export const whatsappService = {
  listAccounts: async () => {
    const res = await api.get("/api/whatsapp/accounts/");
    const results = Array.isArray(res.data?.data) ? res.data.data : [];
    return {
      results,
      count: res.data?.count ?? results.length,
      adminMailAccountIds: Array.isArray(res.data?.admin_mail_account_ids)
        ? res.data.admin_mail_account_ids
        : [],
    };
  },

  startConnection: async (mailAccountId = null) => {
    const res = await api.post("/api/whatsapp/connect/start/", {
      ...(mailAccountId ? { mail_account_id: mailAccountId } : {}),
    });
    return res.data?.data ?? res.data;
  },

  completeConnection: async ({ state, code, phoneNumberId = null, wabaId = null }) => {
    const res = await api.post("/api/whatsapp/connect/complete/", {
      state,
      code,
      ...(wabaId ? { waba_id: wabaId } : {}),
      ...(phoneNumberId ? { phone_number_id: phoneNumberId } : {}),
    });
    return res.data?.data ?? res.data;
  },

  disconnect: async (connectionId) => {
    const res = await api.post(
      `/api/whatsapp/accounts/${connectionId}/disconnect/`,
    );
    return res.data?.data ?? res.data;
  },

  listLeadThreads: async (leadId) => {
    const res = await api.get(`/api/whatsapp/leads/${leadId}/threads/`);
    const results = toResults(res.data);
    return { results, count: res.data?.count ?? results.length };
  },

  listLeadPersons: async (leadId) => {
    const res = await api.get(`/api/whatsapp/leads/${leadId}/persons/`);
    return res.data?.data ?? [];
  },

  createThread: async ({ leadId, whatsappAccountId, personId, phone = null }) => {
    const res = await api.post("/api/whatsapp/threads/", {
      lead_id: leadId,
      whatsapp_account_id: whatsappAccountId,
      person_id: personId,
      ...(phone ? { phone } : {}),
    });
    return { thread: res.data?.data ?? res.data, created: res.status === 201 };
  },

  listMessages: async (threadId, { before = null } = {}) => {
    const res = await api.get(`/api/whatsapp/threads/${threadId}/messages/`, {
      params: before ? { before } : {},
    });
    return {
      results: toResults(res.data),
      hasMore: Boolean(res.data?.has_more),
      thread: res.data?.thread ?? null,
    };
  },

  sendText: async (threadId, body) => {
    const res = await api.post(`/api/whatsapp/threads/${threadId}/messages/`, {
      body,
    });
    return res.data?.data ?? res.data;
  },

  sendMedia: async (threadId, { file, attachmentId, caption = "" }) => {
    const form = new FormData();
    if (file) form.append("file", file, file?.name);
    if (attachmentId) form.append("attachment_id", String(attachmentId));
    if (caption) form.append("caption", caption);
    const res = await api.post(`/api/whatsapp/threads/${threadId}/media/`, form);
    return res.data?.data ?? res.data;
  },

  fetchMediaBlob: async (messageId) => {
    const res = await api.get(`/api/whatsapp/messages/${messageId}/media/`, {
      responseType: "blob",
    });
    return res.data;
  },

  markThreadRead: async (threadId) => {
    const res = await api.post(`/api/whatsapp/threads/${threadId}/read/`);
    return res.data?.data ?? res.data;
  },

  // Sin asignar de WhatsApp oculto a pedido del cliente (16 oct 2026); la API del back también está comentada.
  // listUnassignedThreads: async ({ accountId = null } = {}) => {
  //   const res = await api.get("/api/whatsapp/threads/unassigned/", {
  //     params: accountId ? { account_id: accountId } : {},
  //   });
  //   const results = toResults(res.data);
  //   return { results, count: res.data?.count ?? results.length };
  // },

  listTemplates: async (connectionId, { all = false } = {}) => {
    const res = await api.get(`/api/whatsapp/accounts/${connectionId}/templates/`, {
      params: all ? { status: "all" } : {},
    });
    const results = toResults(res.data);
    return { results, count: res.data?.count ?? results.length };
  },

  createTemplate: async (connectionId, template) => {
    const res = await api.post(
      `/api/whatsapp/accounts/${connectionId}/templates/`,
      template,
    );
    return res.data?.data ?? res.data;
  },

  updateTemplate: async (connectionId, name, template) => {
    const res = await api.put(
      `/api/whatsapp/accounts/${connectionId}/templates/${encodeURIComponent(name)}/`,
      template,
    );
    return res.data?.data ?? res.data;
  },

  listTemplateGroups: async (connectionId) => {
    const res = await api.get(`/api/whatsapp/accounts/${connectionId}/template-groups/`);
    return res.data?.data ?? [];
  },

  createTemplateGroup: async (connectionId, name) => {
    const res = await api.post(`/api/whatsapp/accounts/${connectionId}/template-groups/`, { name });
    return res.data?.data ?? res.data;
  },

  renameTemplateGroup: async (connectionId, groupId, name) => {
    const res = await api.put(`/api/whatsapp/accounts/${connectionId}/template-groups/${groupId}/`, {
      name,
    });
    return res.data?.data ?? res.data;
  },

  deleteTemplateGroup: async (connectionId, groupId) => {
    await api.delete(`/api/whatsapp/accounts/${connectionId}/template-groups/${groupId}/`);
  },

  setTemplateGroup: async (connectionId, name, groupId) => {
    const res = await api.put(
      `/api/whatsapp/accounts/${connectionId}/templates/${encodeURIComponent(name)}/group/`,
      { group_id: groupId },
    );
    return res.data?.data ?? res.data;
  },

  listTags: async (connectionId) => {
    const res = await api.get(`/api/whatsapp/accounts/${connectionId}/tags/`);
    return res.data?.data ?? [];
  },

  getThreadTags: async (threadId, { leadId = null } = {}) => {
    const res = await api.get(`/api/whatsapp/threads/${threadId}/tags/`, {
      params: leadId ? { lead_id: leadId } : {},
    });
    return res.data?.data ?? [];
  },

  deleteTemplate: async (connectionId, name) => {
    await api.delete(
      `/api/whatsapp/accounts/${connectionId}/templates/${encodeURIComponent(name)}/`,
    );
  },

  sendTemplate: async (
    threadId,
    { templateName, language = "", parameters = [], headerParameters = [] },
  ) => {
    const res = await api.post(`/api/whatsapp/threads/${threadId}/template/`, {
      template_name: templateName,
      language,
      parameters,
      header_parameters: headerParameters,
    });
    return res.data?.data ?? res.data;
  },

  // Sin asignar de WhatsApp oculto a pedido del cliente (16 oct 2026); solo lo usaba esa pantalla.
  // assignThread: async (threadId, leadId) => {
  //   const res = await api.post(`/api/whatsapp/threads/${threadId}/assign/`, {
  //     lead_id: leadId,
  //   });
  //   return res.data?.data ?? res.data;
  // },
};

export default whatsappService;
